import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select } from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function State() {
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newState, setNewState] = useState({ name: '', country_id: '' });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = Cookies.get('admin_token');
      const [statesRes, countriesRes] = await Promise.all([
        axios.get(`${API}/master/states`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/master/countries`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setStates(statesRes.data.data || []);
      setCountries(countriesRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newState.name.trim() || !newState.country_id) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/master/states`, newState, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('State added successfully');
      setNewState({ name: '', country_id: '' });
      setOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add state');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this state?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/states/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('State deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete state');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">State</h1>
          <p className="text-muted-foreground">Manage states in the system</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add State
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New State</DialogTitle>
              <DialogDescription>Select country and enter state name</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select
                    value={newState.country_id}
                    onChange={(e) => setNewState({...newState, country_id: e.target.value})}
                    disabled={adding}
                    required
                  >
                    <option value="">Select country</option>
                    {countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>State Name *</Label>
                  <Input
                    value={newState.name}
                    onChange={(e) => setNewState({...newState, name: e.target.value})}
                    placeholder="Enter state name"
                    disabled={adding}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newState.name.trim() || !newState.country_id}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add State'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* States List */}
      <Card>
        <CardHeader>
          <CardTitle>All States ({states.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : states.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No states added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {states.map((state) => (
                <div
                  key={state.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div>
                    <p className="font-medium">{state.name}</p>
                    <p className="text-sm text-muted-foreground">{state.country_name}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(state.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
